/**
 * The order of the two tab strips on the Database page.
 *
 * Paul Jimoulis (Monday 12963781026, CRM-2026-00021, 3 Sep 2026): "the tabs at
 * the top of the screen, can you please make there moveable as they are
 * currently stuck and i want to select the order."
 *
 * Both strips were sorted FOR the agent - the category chips along the top A-Z,
 * the sheet tabs under the grid by their leading number - so the category or
 * sheet they work every morning could never be put first. The agent's own order
 * is saved on their ViewConfig (`group_order` by heading, `list_order` by
 * ProspectList id) and applied here, on top of those defaults.
 *
 * Kept out of DatabasePage.js so the ordering can be tested on its own, the way
 * sortRows.js already is.
 */

// Names and ids alike are matched loosely: a CSV import can spell the same
// suburb differently, and an id arrives as a number from the API but as a
// string once it has been through a URL.
const key = (v) => String(v ?? "").trim().toLowerCase();

/**
 * Put a strip of tabs into the order the agent dragged them into.
 *
 * Anything the saved order has never seen - a category made since, a sheet a
 * CSV import just created - keeps the position it already had and falls in
 * AFTER the tabs that were placed by hand, rather than jumping to the front.
 * That is what the tie-break on the incoming index is for: the default order is
 * still alphabetical (categories) or by leading number (sheets), and dragging
 * one tab must not shuffle the rest.
 */
export const applySavedOrder = (items, saved, keyOf) => {
  const rank = new Map();
  (saved || []).forEach((k, i) => {
    const id = key(k);
    if (id && !rank.has(id)) rank.set(id, i);
  });
  if (!rank.size) return items;
  const at = (it) => {
    const r = rank.get(key(keyOf(it)));
    return r === undefined ? Number.MAX_SAFE_INTEGER : r;
  };
  return items
    .map((it, i) => [it, i])
    .sort((a, b) => at(a[0]) - at(b[0]) || a[1] - b[1])
    .map(([it]) => it);
};

/**
 * One tab dropped onto another: take it out of the strip and put it where the
 * tab it was dropped on sits. Returns null when nothing moved, so a drag that
 * ends where it started never writes to the API.
 */
export const moveInOrder = (keys, from, to) => {
  const i = keys.findIndex((k) => key(k) === key(from));
  const j = keys.findIndex((k) => key(k) === key(to));
  if (i < 0 || j < 0 || i === j) return null;
  const next = [...keys];
  next.splice(j, 0, next.splice(i, 1)[0]);
  return next;
};
